import type { GraphDocument } from '../../entities/graph-document';
import type { JourneyNode } from '../../entities/node';
import type { CandidateOccurrence, DeIdentificationCandidate, DeIdentificationSettings } from '../../entities/ai-assist-config';

const HUMAN_BUCKET_ORDER = 0;
const FIELD_BUCKET_ORDER = 1;
const TAG_BUCKET_ORDER = 2;

interface CandidateAccumulator {
  candidate: DeIdentificationCandidate;
}

export function scanDeidentificationCandidates(
  graph: GraphDocument,
  existingCandidates: DeIdentificationCandidate[] = [],
  humanAliasPrefix = 'Person',
): DeIdentificationCandidate[] {
  const candidateMap = new Map<string, CandidateAccumulator>();
  const sortedHumans = graph.nodes
    .filter((node) => node.type === 'Human' && node.displayName.trim().length > 0)
    .slice()
    .sort((left, right) => left.displayName.localeCompare(right.displayName, 'zh-Hant') || left.nodeId.localeCompare(right.nodeId));
  const humanIndexMap = new Map(sortedHumans.map((node, index) => [node.nodeId, index + 1]));

  for (const node of graph.nodes) {
    scanNode(node, candidateMap, humanIndexMap, humanAliasPrefix);
  }

  return mergeScannedCandidates([...candidateMap.values()].map((entry) => entry.candidate), existingCandidates);
}

export function mergeScannedCandidates(
  nextCandidates: DeIdentificationCandidate[],
  existingCandidates: DeIdentificationCandidate[],
): DeIdentificationCandidate[] {
  const existingById = new Map(existingCandidates.map((candidate) => [candidate.candidateId, candidate]));

  return nextCandidates
    .map((candidate) => {
      const existing = existingById.get(candidate.candidateId);
      if (!existing) {
        return candidate;
      }

      const preserveCustomization = existing.originalValue === candidate.originalValue;
      return {
        ...candidate,
        enabled: preserveCustomization ? existing.enabled : candidate.enabled,
        replacementValue: preserveCustomization ? existing.replacementValue : candidate.replacementValue,
        isCustomized: preserveCustomization ? existing.isCustomized : candidate.isCustomized,
      };
    })
    .sort(compareCandidates);
}

export function fingerprintGraphCandidateSources(graph: GraphDocument): string {
  return graph.nodes
    .map((node) => [
      node.nodeId,
      node.type,
      node.displayName,
      node.summary ?? '',
      node.notes,
      node.tags.join('|'),
      node.customFields.map((field) => `${field.fieldId}:${field.value}`).join('|'),
    ].join('::'))
    .join('\n');
}

export function applyCandidateScanToSettings(
  graph: GraphDocument,
  settings: DeIdentificationSettings,
): DeIdentificationSettings {
  return {
    ...settings,
    candidates: scanDeidentificationCandidates(graph, settings.candidates, settings.humanAliasPrefix),
    updatedAt: new Date().toISOString(),
  };
}

function scanNode(
  node: JourneyNode,
  candidateMap: Map<string, CandidateAccumulator>,
  humanIndexMap: Map<string, number>,
  humanAliasPrefix: string,
) {
  if (node.type === 'Human' && node.displayName.trim().length > 0) {
    addCandidate(candidateMap, {
      candidateId: `human-name:${node.nodeId}`,
      candidateType: 'human-name',
      originalValue: node.displayName.trim(),
      replacementValue: `${humanAliasPrefix}_${String(humanIndexMap.get(node.nodeId) ?? 1).padStart(3, '0')}`,
      defaultReplacementValue: `${humanAliasPrefix}_${String(humanIndexMap.get(node.nodeId) ?? 1).padStart(3, '0')}`,
      enabled: true,
      isCustomized: false,
      sortBucket: 'human',
      sortKey: String(humanIndexMap.get(node.nodeId) ?? 1).padStart(4, '0'),
      occurrences: [
        occurrence(node.nodeId, 'displayName', `${node.type}.displayName`),
      ],
    });
  } else {
    addFieldText(candidateMap, node, 'displayName', node.displayName, `${node.type}.displayName`);
  }

  addFieldText(candidateMap, node, 'summary', node.summary ?? '', `${node.type}.summary`);
  addFieldText(candidateMap, node, 'notes', node.notes, `${node.type}.notes`);
  node.tags.forEach((tag, index) => addTag(candidateMap, node, tag, index));
  node.customFields.forEach((field) =>
    addFieldText(candidateMap, node, `customFields[${field.fieldId}].value`, field.value, `${node.type}.customFields[${field.label}]`),
  );
}

function addFieldText(
  candidateMap: Map<string, CandidateAccumulator>,
  node: JourneyNode,
  fieldPath: string,
  value: string,
  displayLabel: string,
) {
  const originalValue = value.trim();
  if (!originalValue) {
    return;
  }

  addCandidate(candidateMap, {
    candidateId: `field-text:${originalValue}`,
    candidateType: 'field-text',
    originalValue,
    replacementValue: null,
    defaultReplacementValue: null,
    enabled: false,
    isCustomized: false,
    sortBucket: 'field',
    sortKey: originalValue.toLocaleLowerCase(),
    occurrences: [occurrence(node.nodeId, fieldPath, displayLabel)],
  });
}

function addTag(candidateMap: Map<string, CandidateAccumulator>, node: JourneyNode, tag: string, index: number) {
  const originalValue = tag.trim();
  if (!originalValue) {
    return;
  }

  addCandidate(candidateMap, {
    candidateId: `tag:${originalValue}`,
    candidateType: 'tag',
    originalValue,
    replacementValue: null,
    defaultReplacementValue: null,
    enabled: false,
    isCustomized: false,
    sortBucket: 'tag',
    sortKey: originalValue.toLocaleLowerCase(),
    occurrences: [occurrence(node.nodeId, `tags[${index}]`, `${node.type}.tags`)],
  });
}

function addCandidate(candidateMap: Map<string, CandidateAccumulator>, next: DeIdentificationCandidate) {
  const existing = candidateMap.get(next.candidateId);
  if (!existing) {
    candidateMap.set(next.candidateId, { candidate: next });
    return;
  }

  existing.candidate.occurrences.push(...next.occurrences);
}

function occurrence(nodeId: string, fieldPath: string, displayLabel: string): CandidateOccurrence {
  return {
    nodeId,
    fieldPath,
    displayLabel,
  };
}

function compareCandidates(left: DeIdentificationCandidate, right: DeIdentificationCandidate): number {
  return (
    bucketOrder(left.sortBucket) - bucketOrder(right.sortBucket)
    || left.sortKey.localeCompare(right.sortKey, 'zh-Hant')
    || left.originalValue.localeCompare(right.originalValue, 'zh-Hant')
    || left.candidateId.localeCompare(right.candidateId)
  );
}

function bucketOrder(bucket: DeIdentificationCandidate['sortBucket']): number {
  if (bucket === 'human') return HUMAN_BUCKET_ORDER;
  if (bucket === 'field') return FIELD_BUCKET_ORDER;
  return TAG_BUCKET_ORDER;
}
